import React from 'react';
import autosize from 'autosize';
import { routes } from 'constants/config';
import { Icon, Card, Spinner, Button, ButtonGroup } from 'ui';
import RemoteAutoComplete from 'components/shared/RemoteAutoComplete';
import request from 'utils/request_ajax';
import CSRFForm from 'components/shared/CSRFForm';
import AttemptPreview from './AttemptPreview';

class OnDemandPracticeRequestFeedback extends React.Component {
  state = {
    selectedFeedbackContributors: [],
    loading: true,
    attempt: null,
    ziggeoApiKey: null,
    hippoPreviewBase: null,
    notes: '',
    saving: false,
  };

  autosizeRef = React.createRef();

  fetchScenario = () => {
    return request.get(
      routes.ON_DEMAND_PRACTICE.LEARNING.ATTEMPTS.REQUEST_FEEDBACK(
        this.props.scenarioId,
        this.props.attemptId,
      ),
    );
  };

  componentDidMount = () => {
    this.setState({ loading: true });
    this.fetchScenario().then(data => {
      this.setState({ ...data, loading: false });
    });
  };

  componentDidUpdate() {
    if (this.autosizeRef.current) {
      autosize(this.autosizeRef.current);
    }
  }

  renderSuggestion = ({ name }) => {
    return <span className="d-f w-fill">{name}</span>;
  };

  filterSuggestions = feedbackContributors => {
    const { selectedFeedbackContributors } = this.state;
    const selectedFeedbackContributorsIds = selectedFeedbackContributors.map(
      feedbackContributor => feedbackContributor.id,
    );
    if (selectedFeedbackContributorsIds.length === 0)
      return feedbackContributors;
    return feedbackContributors.filter(
      feedbackContributor =>
        !selectedFeedbackContributorsIds.includes(feedbackContributor.id),
    );
  };

  onSuggestionSelected = feedbackContributor => {
    const { selectedFeedbackContributors } = this.state;
    this.setState({
      selectedFeedbackContributors: [
        ...selectedFeedbackContributors,
        feedbackContributor,
      ],
    });
  };

  renderSuggestionAutoComplete = () => {
    const { attempt } = this.state;
    return (
      <RemoteAutoComplete
        url={routes.ON_DEMAND_PRACTICE.LEARNING.ATTEMPTS.FEEDBACK_CONTRIBUTOR_OPTIONS(
          attempt.scenarioId,
          attempt.id,
        )}
        renderSuggestion={this.renderSuggestion}
        getSuggestionValue={suggestion => suggestion.name}
        mapResponseToSuggestions={response =>
          response.feedbackContributorOptions
        }
        onSuggestionSelected={this.onSuggestionSelected}
        placeholder={t(
          'practice.practice_feedback.request_feedback.autocomplete_placeholder',
        )}
        filterSuggestions={this.filterSuggestions}
        className="OnDemandPracticeRequestFeedback-input w-fill mb-12"
      />
    );
  };

  removeFeedbackContributor = id => () => {
    this.setState(state => ({
      selectedFeedbackContributors: state.selectedFeedbackContributors.filter(
        feedbackContributor => feedbackContributor.id !== id,
      ),
    }));
  };

  updateNotes = e => {
    const notes = e.target.value;
    this.setState(
      {
        notes,
      },
      () => {
        if (this.autosizeRef.current) {
          autosize.update(this.autosizeRef.current);
        }
      },
    );
  };

  setSaving = () => {
    this.setState({
      saving: true,
    });
  };

  render() {
    const {
      attempt,
      loading,
      ziggeoApiKey,
      hippoPreviewBase,
      selectedFeedbackContributors,
      notes,
      saving,
    } = this.state;
    if (loading) return <Spinner />;
    return (
      <CSRFForm
        url={routes.ON_DEMAND_PRACTICE.LEARNING.ATTEMPTS.SUBMIT_FEEDBACK_REQUEST(
          attempt.scenarioId,
          attempt.id,
        )}
        method="post"
        className="OnDemandPracticeRequestFeedback"
        onSubmit={this.setSaving}
      >
        <div className="mb-16">
          <a
            className="OnDemandPracticeRequestFeedback-backLink"
            href={routes.ON_DEMAND_PRACTICE.LEARNING.INDEX(1)}
          >
            <Icon name="back" className="mr-4" />
            {t('practice.on_demand_practice.back_to_practice')}
          </a>
        </div>
        <Card>
          <Card.Content padding="large">
            <h1 className="OnDemandPracticeRequestFeedback-title">
              <Icon name="say" className="is-28 mr-8" />
              {t('practice.practice_feedback.request_feedback.title')}
            </h1>
            <div className="d-f m-fd-c">
              <div className="OnDemandPracticeRequestFeedback-preview mr-16 m-m-0">
                <div className="VideoEmbed VideoEmbed-player w-fill pos-r minh-300">
                  <AttemptPreview
                    attempt={attempt}
                    ziggeoApiKey={ziggeoApiKey}
                    hippoPreviewBase={hippoPreviewBase}
                  />
                </div>
              </div>
              <div className="OnDemandPracticeRequestFeedback-form d-f fd-c">
                <label className="mb-8" htmlFor="graderInput">
                  <strong>
                    {t('practice.practice_feedback.request_feedback.reviewers')}
                  </strong>{' '}
                  <em>({t('common.required')})</em>
                </label>
                {this.renderSuggestionAutoComplete()}
                <div className="d-f mb-16 mt-8 fw-w">
                  {selectedFeedbackContributors.map(feedbackContributor => (
                    <span
                      key={feedbackContributor.id}
                      className="d-ib p-4-8 bg-gray-light bdrs-small c-black fw-b fz-12 f-smooth mr-8 mb-8"
                    >
                      {feedbackContributor.name}
                      <input
                        type="hidden"
                        name="feedbackContributorIds[]"
                        value={feedbackContributor.id}
                      />
                      <button
                        type="button"
                        aria-label={t('common.close')}
                        onClick={this.removeFeedbackContributor(
                          feedbackContributor.id,
                        )}
                        className="u-resetBtn"
                      >
                        <Icon name="remove" className="ml-8" />
                      </button>
                    </span>
                  ))}
                </div>
                <label className="mb-8" htmlFor="note">
                  <strong>
                    {t(
                      'practice.practice_feedback.request_feedback.notes_label',
                    )}
                  </strong>
                </label>
                <textarea
                  ref={this.autosizeRef}
                  id="note"
                  name="note"
                  value={notes}
                  onChange={this.updateNotes}
                  className="OnDemandPracticeRequestFeedback-input w-fill h-100"
                  placeholder={t(
                    'practice.practice_feedback.request_feedback.notes_placeholder',
                  )}
                />
              </div>
            </div>
            <ButtonGroup alignEnd className="mt-16">
              <Button
                customColor
                disabled={saving || selectedFeedbackContributors.length === 0}
                type="submit"
              >
                {t('practice.practice_feedback.request_feedback.button')}
              </Button>
            </ButtonGroup>
          </Card.Content>
        </Card>
      </CSRFForm>
    );
  }
}

export default OnDemandPracticeRequestFeedback;
